import React, { useMemo, useState } from "react";

import { Input } from "../Input";
import { Popover, PopoverAnchor, PopoverContent } from "../Popover";

export interface SearchableComboboxOption {
  value: string;
  label: string;
  description?: string;
}

export interface SearchableComboboxProps {
  id?: string;
  ariaLabel: string;
  options: SearchableComboboxOption[];
  placeholder: string;
  disabled?: boolean;
  emptyMessage?: string;
  onSelect: (value: string) => void;
}

export const SearchableCombobox: React.FC<SearchableComboboxProps> = ({
  id,
  ariaLabel,
  options,
  placeholder,
  disabled = false,
  emptyMessage = "No results found.",
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const listboxId = React.useId();

  const normalizedSearch = searchTerm.trim().toLocaleLowerCase();
  const filteredOptions = useMemo(
    () =>
      options.filter((option) => {
        if (!normalizedSearch) return true;

        const searchableText = [option.label, option.description]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();

        return searchableText.includes(normalizedSearch);
      }),
    [normalizedSearch, options],
  );

  return (
    <Popover
      open={menuOpen}
      onOpenChange={(nextOpen) => {
        setMenuOpen(nextOpen);
        if (!nextOpen) setSearchTerm("");
      }}
    >
      <PopoverAnchor asChild>
        <Input
          id={id}
          type="search"
          aria-label={ariaLabel}
          role="combobox"
          aria-controls={menuOpen && !disabled ? listboxId : undefined}
          aria-expanded={menuOpen && !disabled}
          aria-autocomplete="list"
          aria-haspopup="listbox"
          placeholder={placeholder}
          disabled={disabled}
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          onFocus={() => setMenuOpen(!disabled)}
          onClick={() => setMenuOpen(!disabled)}
          onBlur={() => setMenuOpen(false)}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setMenuOpen(false);
              event.currentTarget.blur();
            }
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        id={listboxId}
        role="listbox"
        align="start"
        sideOffset={2}
        onOpenAutoFocus={(event) => event.preventDefault()}
        onMouseDown={(event) => event.preventDefault()}
        className="z-40 max-h-[188px] w-[var(--radix-popper-anchor-width)] overflow-y-auto rounded-md border-zinc-900 bg-zinc-900 p-0 shadow-[0_18px_50px_black/45]"
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => {
                onSelect(option.value);
                setSearchTerm("");
                setMenuOpen(false);
              }}
              className="flex w-full cursor-pointer items-center gap-2.5 border-0 border-b border-zinc-900 bg-transparent p-3 text-left text-inherit hover:bg-zinc-800"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-zinc-20 leading-tight">
                  {option.label}
                </span>
                {option.description ? (
                  <span className="block truncate text-[11px] text-zinc-500 leading-tight">
                    {option.description}
                  </span>
                ) : null}
              </span>
            </button>
          ))
        ) : (
          <div className="px-3 py-[18px] text-center text-sm text-zinc-500">
            {emptyMessage}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

SearchableCombobox.displayName = "SearchableCombobox";

export default SearchableCombobox;
